import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { buildUnsignedRetainerPdf } from '../utils/unsignedRetainer';

const API_URL = process.env.REACT_APP_API_URL;

function authorizationHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
}

async function responseData(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'The request could not be completed.');
  }
  return data;
}

const amountText = (value) => value == null ? '' : String(value);

function UnsignedRetainerGenerator({ caseId }) {
  const [defaults, setDefaults] = useState(null);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [defaultsError, setDefaultsError] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [amounts, setAmounts] = useState({ totalAttorneyFee: '', retainerAmount: '', preFilingFee: '', filingFeeAmount: '' });
  const mounted = useRef(false);
  const defaultsRequest = useRef(0);
  const generationRequest = useRef(null);
  const busy = useRef(false);
  const generatedUrl = useRef(null);

  const clearGenerated = useCallback(() => {
    if (generatedUrl.current) URL.revokeObjectURL(generatedUrl.current);
    generatedUrl.current = null;
    setGenerated(null);
  }, []);

  const loadDefaults = useCallback(async () => {
    const requestId = ++defaultsRequest.current;
    setDefaultsLoading(true);
    setDefaultsError('');
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/retainer-defaults/`, {
        credentials: 'include',
        headers: authorizationHeaders(),
      });
      const data = await responseData(response);
      if (!mounted.current || defaultsRequest.current !== requestId) return;
      if (!data || Array.isArray(data) || (data.case_id && data.case_id !== caseId)) {
        throw new Error('The retainer defaults could not be verified for this case.');
      }
      setDefaults(data);
      setAmounts({
        totalAttorneyFee: amountText(data.total_attorney_fees ?? data.pre_filing_fees),
        retainerAmount: amountText(data.pre_filing_fees ?? data.total_attorney_fees),
        preFilingFee: amountText(data.pre_filing_fees),
        filingFeeAmount: amountText(data.filing_fee_amount),
      });
      clearGenerated();
    } catch (failure) {
      if (mounted.current && defaultsRequest.current === requestId) {
        setDefaultsError(failure.message || 'The unsigned-retainer defaults could not be loaded.');
      }
    } finally {
      if (mounted.current && defaultsRequest.current === requestId) setDefaultsLoading(false);
    }
  }, [caseId, clearGenerated]);

  useEffect(() => {
    mounted.current = true;
    loadDefaults();
    return () => {
      mounted.current = false;
      defaultsRequest.current += 1;
      generationRequest.current = null;
      busy.current = false;
      if (generatedUrl.current) URL.revokeObjectURL(generatedUrl.current);
      generatedUrl.current = null;
    };
  }, [loadDefaults]);

  const chapter = String(defaults?.chapter || '');
  const filingDebtors = (Array.isArray(defaults?.debtors) ? defaults.debtors : [])
    .filter((debtor) => debtor.is_filing !== false)
    .sort((first, second) => Number(Boolean(second.is_primary)) - Number(Boolean(first.is_primary)));
  const debtorNames = filingDebtors.map((debtor) => String(debtor.debtor_full_name || '').trim()).filter(Boolean);
  const numericAmount = (value) => {
    const text = String(value).replace(/[$,]/g, '').trim();
    return text && Number.isFinite(Number(text)) ? Number(text) : null;
  };
  const total = numericAmount(amounts.totalAttorneyFee);
  const beforeFiling = numericAmount(amounts.preFilingFee);
  const remaining = total == null || beforeFiling == null ? null : total - beforeFiling;

  const updateAmount = (field, value) => {
    setAmounts((current) => ({ ...current, [field]: value }));
    setGenerationError('');
    clearGenerated();
  };

  const generate = async (event) => {
    event.preventDefault();
    if (busy.current || defaultsLoading || !defaults) return;
    const requestId = {};
    generationRequest.current = requestId;
    busy.current = true;
    setGenerating(true);
    setGenerationError('');
    const isCurrent = () => mounted.current && generationRequest.current === requestId;
    try {
      if (!['7', '13'].includes(chapter)) throw new Error('Unsigned retainers are available for Chapter 7 and Chapter 13 cases.');
      if (!debtorNames.length) throw new Error('No filing debtors were found for this retainer.');
      const templateUrl = `${process.env.PUBLIC_URL || ''}/docs/retainer_ch${chapter}.pdf`;
      const templateResponse = await fetch(templateUrl);
      if (!templateResponse.ok) throw new Error('The retainer PDF template could not be loaded. Please try again.');
      const templateBytes = await templateResponse.arrayBuffer();
      if (!isCurrent()) return;
      const bytes = await buildUnsignedRetainerPdf({
        templateBytes,
        chapter,
        caseId,
        debtorNames,
        retainerAmount: amounts.retainerAmount,
        totalAttorneyFee: amounts.totalAttorneyFee,
        preFilingFee: chapter === '7' ? amounts.totalAttorneyFee : amounts.preFilingFee,
        filingFeeAmount: amounts.filingFeeAmount,
      });
      if (!isCurrent()) return;
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (generatedUrl.current) URL.revokeObjectURL(generatedUrl.current);
      generatedUrl.current = url;
      const names = debtorNames.join('_').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 120);
      setGenerated({ url, filename: `Unsigned_Retainer_Ch${chapter}_${names}.pdf` });
    } catch (failure) {
      if (isCurrent()) setGenerationError(failure.message || 'The unsigned retainer could not be generated.');
    } finally {
      if (isCurrent()) {
        setGenerating(false);
        generationRequest.current = null;
        busy.current = false;
      }
    }
  };

  return (
    <section aria-labelledby="unsigned-retainer-heading" className="rounded-lg border border-indigo-100 bg-white p-5 shadow-sm">
      <h2 id="unsigned-retainer-heading" className="text-lg font-semibold text-gray-900">Prepare an unsigned retainer for in-person signing</h2>
      <p className="mt-1 text-sm text-gray-600">Generate a printable PDF with blank dates and signature lines. These draft amounts do not record payments, save an agreement, or change anyone’s signing status.</p>
      {defaultsLoading ? <p className="mt-4 text-sm text-gray-600">Loading unsigned-retainer defaults…</p> : defaultsError ? (
        <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
          <p role="alert" className="text-sm text-amber-900">{defaultsError}</p>
          <button type="button" onClick={loadDefaults} className="mt-2 rounded border bg-white px-3 py-2 text-sm font-medium">Retry retainer defaults</button>
        </div>
      ) : defaults && (
        <form onSubmit={generate}>
          <p className="mt-4 text-sm font-medium text-gray-800">Chapter {chapter}{defaults.case_number ? ` · ${defaults.case_number}` : ''}</p>
          <ul className="mt-2 text-sm text-gray-700" aria-label="Filing debtors on the unsigned retainer">
            {filingDebtors.map((debtor, index) => <li key={debtor.debtor_id}>Debtor {index + 1}: {debtor.debtor_full_name}</li>)}
          </ul>
          {!debtorNames.length && <p role="alert" className="mt-2 text-sm text-amber-800">No filing debtors found. Check the case information before preparing this retainer.</p>}
          {!['7', '13'].includes(chapter) && <p role="alert" className="mt-2 text-sm text-amber-800">Unsigned retainers are available for Chapter 7 and Chapter 13 cases.</p>}
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {[
              ['totalAttorneyFee', 'Total attorney fee'],
              ['retainerAmount', 'Retainer amount paid (for this draft)'],
              ['filingFeeAmount', 'Filing fee'],
              ...(chapter === '13' ? [['preFilingFee', 'Fee due before filing']] : []),
            ].map(([field, label]) => <div key={field}>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
                <input type="text" inputMode="decimal" required value={amounts[field]} disabled={generating} onChange={(event) => updateAmount(field, event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </label>
              {field === 'retainerAmount' && <button type="button" disabled={generating} onClick={() => updateAmount('retainerAmount', amounts.totalAttorneyFee)} className="mt-2 text-xs font-medium text-indigo-700 underline disabled:opacity-50">Use full attorney fee</button>}
            </div>)}
          </div>
          {chapter === '13' && <p className="mt-3 text-sm text-gray-600">Remaining attorney fee through the plan: <span className="font-semibold">{remaining == null ? 'Enter the total and prefiling fee amounts.' : remaining < 0 ? 'The prefiling fee cannot exceed the total attorney fee.' : `$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span></p>}
          {chapter === '7' && <p className="mt-3 text-sm text-gray-600">The Chapter 7 fee due before filing will be the total attorney fee above.</p>}
          <p className="mt-3 text-sm text-gray-600">All filing debtors listed above will appear on the PDF, regardless of the debtor selected in the upload module below. Dates and signatures are left blank.</p>
          {generationError && <p role="alert" className="mt-3 text-sm text-red-700">{generationError}</p>}
          <button type="submit" disabled={generating || !debtorNames.length || !['7', '13'].includes(chapter)} className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            <DocumentTextIcon className="h-5 w-5" />{generating ? 'Preparing unsigned PDF…' : 'Generate unsigned retainer PDF'}
          </button>
          {generated && <div className="mt-4 flex flex-wrap items-center gap-4 rounded border border-green-200 bg-green-50 p-3">
            <span className="text-sm font-medium text-green-800">Unsigned PDF ready. Dates and signatures are blank.</span>
            <a href={generated.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-indigo-700 underline">Open / print unsigned retainer</a>
            <a href={generated.url} download={generated.filename} className="text-sm font-semibold text-indigo-700 underline">Download unsigned retainer</a>
          </div>}
        </form>
      )}
    </section>
  );
}

export default function FirmRetainerPage() {
  const { caseId } = useParams();
  const fileInputRef = useRef(null);
  const [agreements, setAgreements] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [selectedDebtorId, setSelectedDebtorId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadRetainers = useCallback(async () => {
    const [agreementResponse, statusResponse] = await Promise.all([
      fetch(`${API_URL}/case/${caseId}/retainer-agreement/`, {
        credentials: 'include',
        headers: authorizationHeaders(),
      }),
      fetch(`${API_URL}/case/${caseId}/retainer-status/`, {
        credentials: 'include',
        headers: authorizationHeaders(),
      }),
    ]);
    const [agreementData, statusData] = await Promise.all([
      responseData(agreementResponse),
      responseData(statusResponse),
    ]);
    const nextAgreements = Array.isArray(agreementData) ? agreementData : [];
    const nextDebtors = Array.isArray(statusData) ? statusData : [];
    setAgreements(nextAgreements);
    setDebtors(nextDebtors);
    setSelectedDebtorId((current) => {
      if (current && nextDebtors.some((debtor) => String(debtor.debtor_id) === String(current))) {
        return current;
      }
      const unsignedDebtor = nextDebtors.find((debtor) => !debtor.signed);
      return String((unsignedDebtor || nextDebtors[0])?.debtor_id || '');
    });
  }, [caseId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadRetainers()
      .catch((err) => {
        if (active) setError(err.message || 'Failed to load the retainer agreements.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [loadRetainers]);

  const selectedDebtor = debtors.find(
    (debtor) => String(debtor.debtor_id) === String(selectedDebtorId),
  );

  const handleUpload = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedDebtorId) {
      setError('Select the debtor who signed the agreement.');
      return;
    }
    if (selectedDebtor?.signed) {
      setError('This debtor already has a signed retainer on file. Delete it first if it needs to be replaced.');
      return;
    }
    if (!selectedFile) {
      setError('Choose the scanned retainer PDF.');
      return;
    }

    const formData = new FormData();
    formData.append('debtor_id', selectedDebtorId);
    formData.append('upload', selectedFile);
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/retainer-agreement/`, {
        method: 'POST',
        credentials: 'include',
        headers: authorizationHeaders(),
        body: formData,
      });
      await responseData(response);
      setSuccess(`Signed retainer uploaded for ${selectedDebtor?.debtor_full_name || 'the debtor'}.`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadRetainers();
    } catch (err) {
      setError(err.message || 'The signed retainer could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (agreement) => {
    if (!window.confirm(`Delete the signed retainer for ${agreement.debtor_full_name || 'this debtor'}?`)) {
      return;
    }
    setError('');
    setSuccess('');
    setDeletingId(agreement.id);
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/retainer-agreement/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          ...authorizationHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: agreement.id }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'The retainer agreement could not be deleted.');
      }
      setSuccess('The signed retainer was deleted.');
      await loadRetainers();
    } catch (err) {
      setError(err.message || 'The retainer agreement could not be deleted.');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Loading retainer agreements…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Retainer Agreements</h1>
        <p className="mt-1 text-sm text-gray-600">
          Review electronically signed agreements or upload a physically signed and scanned PDF.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircleIcon className="h-5 w-5" />
          {success}
        </div>
      )}

      <UnsignedRetainerGenerator key={caseId} caseId={caseId} />

      <form onSubmit={handleUpload} className="rounded-lg border border-indigo-100 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
            <ArrowUpTrayIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload a physically signed retainer</h2>
            <p className="text-sm text-gray-600">
              Select the filing debtor whose signature appears on the scanned agreement.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Debtor</span>
            <select
              value={selectedDebtorId}
              onChange={(event) => setSelectedDebtorId(event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {debtors.length === 0 && <option value="">No filing debtors found</option>}
              {debtors.map((debtor, index) => (
                <option key={debtor.debtor_id} value={debtor.debtor_id}>
                  {index === 0 ? 'Debtor 1 — ' : 'Debtor 2 — '}
                  {debtor.debtor_full_name}
                  {debtor.signed ? ' (signed agreement on file)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Scanned agreement PDF</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
              className="block w-full rounded-md border border-gray-300 bg-white text-sm text-gray-700 file:mr-4 file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:font-medium file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </label>
        </div>

        {selectedDebtor?.signed && (
          <p className="mt-3 text-sm text-amber-700">
            This debtor already has a signed agreement. Delete the existing agreement before uploading a replacement.
          </p>
        )}

        <button
          type="submit"
          disabled={uploading || !selectedDebtorId || !selectedFile || selectedDebtor?.signed}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowUpTrayIcon className="h-5 w-5" />
          {uploading ? 'Uploading…' : 'Upload signed retainer'}
        </button>
      </form>

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Signed retainers on file</h2>
        {agreements.length === 0 ? (
          <div className="mt-4 rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No signed retainer agreements have been received yet.
          </div>
        ) : (
          <div className="mt-4 divide-y divide-gray-100">
            {agreements.map((agreement) => (
              <div key={agreement.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <DocumentTextIcon className="mt-0.5 h-6 w-6 flex-none text-indigo-500" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">
                      {agreement.debtor_full_name || 'Unknown signer'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {agreement.debtor_email || 'No email'} · {new Date(agreement.created_at).toLocaleString()}
                    </div>
                    <div className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                      {agreement.upload_source === 'firm' ? 'Uploaded by firm' : 'Electronically signed by client'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:flex-none">
                  <a
                    href={agreement.upload_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    View PDF
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDelete(agreement)}
                    disabled={deletingId === agreement.id}
                    className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {deletingId === agreement.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
